import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { WorkflowsHelperService } from '../../services/workflows-helper.service';
import { firstMissingLocaleTextId, localeTextRequireAllLocalesWhenAnyFilledValidator } from '../../utils/workflow-locale-text.util';
import { IFormFieldPreset } from './form-field-presets';

export const FORM_STEP_FIELD_TITLE_ANCHOR_ID = 'form-step-field-title';

/** Presets con input visible de «Etiqueta del campo» (no select/checkbox/legal). */
export function presetShowsFieldTitleEditor(preset?: IFormFieldPreset | null): boolean {
	if (!preset) {
		return false;
	}

	return !(
		preset.usesOptionsEditor ||
		preset.usesRichTextOptionsEditor ||
		preset.usesYesNoPreview ||
		preset.usesLegalFieldsConfig
	);
}

/**
 * Rellena `fields[].title` con el nombre del preset al serializar si el operador no lo editó.
 * Excluye selección única/múltiple: en Figma solo aparecen las opciones (NIF-550).
 */
export function presetAppliesSerializedFieldTitleFallback(preset?: IFormFieldPreset | null): boolean {
	if (!preset || preset.fieldMetadataLocked || presetShowsFieldTitleEditor(preset)) {
		return false;
	}

	if (
		preset.usesOptionsEditor &&
		!preset.usesRichTextOptionsEditor &&
		!preset.usesLegalFieldsConfig
	) {
		return false;
	}

	return true;
}

/** Si la etiqueta del campo es obligatoria cuando el editor está visible (Figma: nunca). */
export function presetRequiresFieldTitle(_preset?: IFormFieldPreset | null): boolean {
	return false;
}

export function getFormStepFieldTitleControl(fieldGroup: FormGroup, localeId: string): FormControl {
	return fieldGroup.get('title')?.get(localeId) as FormControl;
}

function clearFieldTitleValidators(titleGroup: FormGroup | null | undefined): void {
	if (!titleGroup) {
		return;
	}

	Object.values(titleGroup.controls).forEach((control) => {
		control.clearValidators();
		control.updateValueAndValidity({ emitEvent: false });
	});
	titleGroup.updateValueAndValidity({ emitEvent: false });
}

/** Etiqueta del campo opcional; si hay texto en un idioma, obligatorio completar todos (NIF-550). */
export function applyFormStepFieldTitleValidators(
	fieldsArray: FormArray,
	_workflowsHelper: WorkflowsHelperService,
	preset?: IFormFieldPreset | null,
): void {
	fieldsArray.controls.forEach((fieldControl) => {
		const titleGroup = (fieldControl as FormGroup).get('title') as FormGroup;
		clearFieldTitleValidators(titleGroup);

		if (presetShowsFieldTitleEditor(preset) && !preset?.fieldMetadataLocked) {
			titleGroup.setValidators([localeTextRequireAllLocalesWhenAnyFilledValidator()]);
			titleGroup.updateValueAndValidity({ emitEvent: false });
		}
	});
}

export function markFormStepFieldTitlesTouched(fieldsArray: FormArray): void {
	fieldsArray.controls.forEach((fieldControl) => {
		(fieldControl as FormGroup).get('title')?.markAllAsTouched();
	});
}

export function firstInvalidFormStepFieldTitleRef(
	fieldsArray: FormArray,
	allowedLocales: readonly string[],
): { localeId: string; fieldIndex: number } | null {
	for (let fieldIndex = 0; fieldIndex < fieldsArray.length; fieldIndex += 1) {
		const titleGroup = (fieldsArray.at(fieldIndex) as FormGroup).get('title') as FormGroup | null;

		if (!titleGroup?.invalid) {
			continue;
		}

		const missingLocaleId = firstMissingLocaleTextId(titleGroup, allowedLocales);

		if (missingLocaleId) {
			return { localeId: missingLocaleId, fieldIndex };
		}
	}

	return null;
}

export function formStepFieldTitleHasError(
	reactiveFormService: ReactiveFormsService,
	fieldGroup: FormGroup,
	localeId: string,
): boolean {
	const localeControl = getFormStepFieldTitleControl(fieldGroup, localeId);
	if (reactiveFormService.hasError(localeControl)) {
		return true;
	}

	const titleGroup = fieldGroup.get('title') as FormGroup | null;
	if (!titleGroup || !reactiveFormService.hasError(titleGroup as unknown as Partial<FormControl>)) {
		return false;
	}

	return (localeControl.value ?? '').toString().trim().length === 0;
}

export function formStepFieldTitleErrorMessage(
	reactiveFormService: ReactiveFormsService,
	fieldGroup: FormGroup,
	localeId: string,
	requiredMessage: string,
): string {
	const localeControl = getFormStepFieldTitleControl(fieldGroup, localeId);
	const controlErrorMessage = reactiveFormService.hasMsgError(localeControl);
	if (controlErrorMessage) {
		return controlErrorMessage;
	}

	if (formStepFieldTitleHasError(reactiveFormService, fieldGroup, localeId)) {
		return requiredMessage;
	}

	return '';
}

export function focusFormStepFieldTitle(): void {
	setTimeout(() => {
		document.getElementById(FORM_STEP_FIELD_TITLE_ANCHOR_ID)?.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest',
		});
		const focusableInput = document
			.getElementById(FORM_STEP_FIELD_TITLE_ANCHOR_ID)
			?.querySelector('input, textarea') as HTMLElement | null;
		focusableInput?.focus?.();
	}, 320);
}
